import { useCallback, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

import type { LocationResultData } from '../../screens/LocationsScreen/GraphQL';

export const useLocationWarningModal = () => {
  const checkShowWarning = useCallback(
    async (location?: LocationResultData) => {
      if (
        !location ||
        !location.showWarningDialog ||
        !location.warningDialogTitle
      ) {
        return false;
      }

      const LOCATION_WARNING_DIALOG_KEY = `restaurant-warning-dialog-timeout-${location.id}`;
      const hasCookie = await getLocationWarningCookie(
        LOCATION_WARNING_DIALOG_KEY,
      );

      return !hasCookie;
    },
    [],
  );

  const setLocationWarningCookie = useCallback(
    async (location?: LocationResultData) => {
      if (!location) {
        return;
      }

      const LOCATION_WARNING_DIALOG_KEY = `restaurant-warning-dialog-timeout-${location.id}`;
      // timeout is specified in seconds
      // "0" means "always show"
      // "-1" means "only show once"
      const expirationTimeout = location.warningDialogTimeout ?? 0;

      if (expirationTimeout !== 0) {
        const expireDate =
          expirationTimeout === -1
            ? new Date(Date.now() + 1000 * 3600 * 24 * 1000)
            : new Date(Date.now() + expirationTimeout * 1000);
        const cookieData = JSON.stringify({ expiration: expireDate });

        await AsyncStorage.setItem(LOCATION_WARNING_DIALOG_KEY, cookieData);
      }
    },
    [],
  );

  // ──────────────── Modal Visibility Controls ────────────────
  const [isShownLocationWarningModal, setIsShownLocationWarningModal] =
    useState(false);
  const showLocationWarningModal = useCallback(() => {
    setIsShownLocationWarningModal(true);
  }, []);
  const hideLocationWarningModal = useCallback(() => {
    setIsShownLocationWarningModal(false);
  }, []);

  return {
    isShownLocationWarningModal,
    showLocationWarningModal,
    hideLocationWarningModal,
    setLocationWarningCookie,
    checkShowWarning,
  };
};

// ----------------- Helpers ------------------
const getLocationWarningCookie = async (key: string) => {
  const cookie = await AsyncStorage.getItem(key);

  if (!cookie) {
    return false;
  }

  const expiresAt: string = JSON.parse(cookie).expiration || new Date();
  const isExpired = Date.now() >= new Date(expiresAt).getTime();

  return !isExpired;
};
